import type { MeetingRecord } from 'BrokerTsLib/src/dtos'
import type { PropsWithChildren } from 'react'
import { createContext, useContext } from 'react'
import type {
  QueryObserverResult,
  RefetchOptions,
} from '@tanstack/react-query'

import { useGetMeetingRecord, useSaveMeetingRecord } from '../hooks/useWizard'
import { useSaveMeetingRecord as useSaveMeetingRecordAddon } from '../hooks/useWizardAddon'

type MeetingRecordCtxProps = {
  meetingRecord?: MeetingRecord
  isLoading: boolean
  refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<MeetingRecord, any>>
  save: (meetingRecord: MeetingRecord) => Promise<MeetingRecord>
  isSaving: boolean
}

const meetingRecordContext = createContext<MeetingRecordCtxProps>(
  {} as MeetingRecordCtxProps,
)

export function MeetingRecordProvider({
  children,
  insuranceParamsId,
  addon,
}: PropsWithChildren & { insuranceParamsId: string; addon?: boolean }) {
  const { isLoading, isFetching, data, refetch } = useGetMeetingRecord(
    insuranceParamsId,
    { enabled: false, gcTime: 0 },
  )
  const { mutateAsync: saveMeetingRecord, isPending: isSaving } =
    useSaveMeetingRecord()

  const { mutateAsync: saveMeetingRecordAddon, isPending: isSavingAddon } =
    useSaveMeetingRecordAddon()

  return (
    <meetingRecordContext.Provider
      value={{
        isLoading: isFetching || isLoading,
        refetch: refetch,
        meetingRecord: data,
        save: addon ? saveMeetingRecordAddon : saveMeetingRecord,
        isSaving: addon ? isSavingAddon : isSaving,
      }}
    >
      {children}
    </meetingRecordContext.Provider>
  )
}

export const useMeetingRecord = () => {
  const context = useContext(meetingRecordContext)

  if (context === undefined || Object.keys(context).length == 0) {
    throw new Error(
      'useMeetingRecordContext must be used within a MeetingRecordProvider!',
    )
  }

  return context
}
